import { createContext, useContext } from "react";
import { createContextProvider } from "./widgets";
import mime from "mime";
import { ContentType } from "@/api";
import axios, { AxiosProgressEvent } from "axios";
import { useChatMessages } from "./chatMessages";
import { useCurrentMeeting } from "../contexts/currentMeeting";
import { useDispatch } from "react-redux";
import { clearFileUploadProgress, setFileUploadProgress } from "./redux";
import { generateImageThumbnail } from "./tools";
import { FileMessageContent } from "./types";
import { useChattoHttpQuery } from "conference/apollo-client/chatto/hooks";
import { FileUploadConfigQuery, FileUploadConfigDocument } from "./queries-chatto.generated";

type FileUploaderResult = {
    data: {
        file_uri: string;
        preview_uri?: string;
    };
};

const useFileUploaderFactory = () => {
    const { sendMessage, updateMessage } = useChatMessages();
    const currentMeeting = useCurrentMeeting();
    const dispatch = useDispatch();
    const configQuery = useChattoHttpQuery<FileUploadConfigQuery>(FileUploadConfigDocument);

    const upload = async (file: File) => {
        const content: FileMessageContent = {
            filename: file.name,
            metadata: {
                mime_type: mime.getType(file.name) || "",
                size: file.size,
            },
        };
        const messageId = await sendMessage(JSON.stringify(content), ContentType.File);

        dispatch(setFileUploadProgress({ messageId, uploaded: 0 }));

        const formData = new FormData();
        formData.append("file", file);
        if (file.type.startsWith("image/")) formData.append("preview", await generateImageThumbnail(file, 300));

        const res = await axios.post<FileUploaderResult>(window.chattoBaseUrl + "/v1/files", formData, {
            headers: {
                "Content-Type": "multipart/form-data",
                Authorization: `Invitation ${currentMeeting.selfIdentifier}`,
            },
            onUploadProgress: (progressEvent: AxiosProgressEvent) => {
                dispatch(setFileUploadProgress({ messageId, uploaded: progressEvent.loaded }));
            },
        });
        dispatch(clearFileUploadProgress({ messageId }));

        const message: Partial<FileMessageContent> = {
            file_url: res.data.data.file_uri,
        };

        if (res.data.data.preview_uri) message.preview_url = res.data.data.preview_uri;

        await updateMessage(messageId, JSON.stringify(message));
    };

    return {
        upload,
        enabled: configQuery.data?.serverInfo?.fileUpload?.enabled ?? false,
        maxSize: configQuery.data?.serverInfo?.fileUpload?.maximumFileSize ?? 0,
    };
};

const FileUploaderFactoryContext = createContext(undefined);

export const useFileUploader = (): ReturnType<typeof useFileUploaderFactory> => useContext(FileUploaderFactoryContext);

export const FileUploaderFactoryContextProvider = createContextProvider(
    FileUploaderFactoryContext,
    useFileUploaderFactory,
);
